import instantsearch from 'instantsearch.js';
import {
  searchBox,
  configure,
  hits,
  clearRefinements,
  refinementList,
  stats,
  pagination,
} from 'instantsearch.js/es/widgets';
import searchClient from './searchClient';
import SearchPage from './search-page';
import { isExternalUrl } from '../util';

const containers = {
  searchbox: '#resources-search-searchbox',
  stats: '#resources-search-stats',
  hitsContainer: '#resources-search-hits-container',
  hits: '#resources-search-hits',
  pagination: '#resources-search-pagination',
  refinementListAudiences: '#resources-search-refinement-list-audiences',
  refinementListDepartments: '#resources-search-refinement-list-department',
  refinementListTypes: '#resources-search-refinement-list-types',
  refinementListTopics: '#resources-search-refinement-list-topics',
  clearRefinements: '#resources-search-clear-refinements',
};

class AlgoliaResourcesSearch extends SearchPage {
  constructor(indexName) {
    super(indexName);

    this.indexName = indexName;

    this.search = instantsearch({
      indexName: this.indexName,
      routing: true,
      searchClient,
      // https://www.algolia.com/doc/api-reference/widgets/instantsearch/js/#widget-param-searchfunction
      searchFunction: helper => {
        helper.search();
      },
    });

    this.initialize();
    this.search.start();

    // Handle back button route changes
    window.onpopstate = event => {
      // If the input is still focused, change value of input to the current query param
      if (document.activeElement.classList.contains(this.searchInputClassName)) {
        const inputEl = document.querySelector(`.${this.searchInputClassName}`);
        if (event.state) {
          inputEl.value = event.state[this.indexName].query;
        } else {
          inputEl.value = this.queryParam;
        }
      }
    };
  }

  initialize() {
    // https://www.algolia.com/doc/api-reference/widgets/js/
    this.search.addWidgets([
      searchBox({
        container: containers.searchbox,
        placeholder: 'Search resources',
        showSubmit: false,
        cssClasses: {
          input: this.searchInputClassName,
        },
        templates: {
          reset: `
            <svg class="search__reset">
              <use xlink:href="#icon-x"></use>
            </svg>
          `,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/configure/js/
      // Parameter options: https://www.algolia.com/doc/api-reference/search-api-parameters/
      configure({
        hitsPerPage: this.hitsPerPage,
        distinct: true,
        // Force faceting to be applied after de-duplication (via the Distinct setting)
        facetingAfterDistinct: true,
        // Filter the query with facet type of faculty or student
        // https://www.algolia.com/doc/api-reference/api-parameters/filters/#facet-filters
        filters: 'type:resource',
      }),

      // https://www.algolia.com/doc/api-reference/widgets/stats/js/
      stats({
        container: containers.stats,
        templates: {
          text: data => {
            let count = '';

            const pagination = document.querySelector(containers.pagination);
            this.toggleHide(pagination, data.nbHits <= this.hitsPerPage);

            if (data.hasManyResults) {
              count += `${data.nbHits} results found`;
            } else if (data.hasOneResult) {
              count += `1 result found`;
            } else {
              count += `<span class="search__stats-no-results">No results found.</span>`;
            }

            return `${count}`;
          },
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/hits/js/
      hits({
        container: containers.hits,
        escapeHTML: false,
        templates: {
          item(hit) {
            const isFile = hit.resource_link_type === 'File';
            const isExternal = isExternalUrl(hit.resource_link);
            const openInNewTab =
              isFile || isExternal ? ' target="_blank" rel="noopener noreferrer' : '';
            return `
                <article>
                  <a href="${hit.resource_link ? hit.resource_link : '#'}"${openInNewTab}">
                    <span class="hit-title">
                      <strong>
                        ${instantsearch.highlight({ attribute: 'title', hit })}
                      </strong>
                    </span>
                    <span class="hit-source">${hit.resource_source}</span>
                    <svg class="hit-${isFile ? 'document' : 'arrow'}">
                      <use xlink:href="#icon-${isFile ? 'document' : 'arrow'}"></use>
                    </svg>
                  </a>
                </article>
              `;
          },
        },
        transformItems: items => {
          // Hide pagination and results if no results
          this.toggleHide(this.results, items.length < 1);
          if (window.innerWidth <= 768 && items.length > 0) {
            this.toastActive();
          }
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/pagination/js/
      pagination({
        container: containers.pagination,
        showFirst: false,
        showLast: false,
        scrollTo: 'main',
        templates: {
          previous: `
            <span class="visually-hidden">Previous page</span>
            <svg class="arrow prev">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
          next: `
            <span class="visually-hidden">Next page</span>
            <svg class="arrow next">
              <use xlink:href="#icon-arrow"></use>
            </svg>
          `,
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/refinement-list/js/
      // Audiences
      refinementList({
        container: containers.refinementListAudiences,
        attribute: 'audiences',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListAudiences)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // School or Department
      refinementList({
        container: containers.refinementListDepartments,
        attribute: 'departments',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListDepartments)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // Resource Types
      refinementList({
        container: containers.refinementListTypes,
        attribute: 'resource_types',
        limit: 100, // set to arbitrary large number to prevent default limit of 10
        sortBy: this.alphaSortRefinements,
        transformItems: items => {
          // Hide refinement list if there are no results
          const refList = document
            .querySelector(containers.refinementListTypes)
            .closest('.js--accordion');

          this.updateRefListClasses(items, refList);

          this.toggleHide(refList, items.length < 1);
          return items;
        },
      }),

      // https://www.algolia.com/doc/api-reference/widgets/clear-refinements/js/
      clearRefinements({
        container: containers.clearRefinements,
        templates: {
          resetLabel: 'Clear filters',
        },
      }),
    ]);
  }

  static init() {
    new AlgoliaResourcesSearch();
  }
}

export default AlgoliaResourcesSearch;
